import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OutputCard } from "@/components/OutputCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSignature, Scale, Star, Users, Megaphone, MailPlus, Sparkles, RefreshCw, Gauge, SplitSquareHorizontal, Heart, CalendarClock } from "lucide-react";

const TOOLS = [
  { id: "offer_letter", name: "Offer Letter Writer", icon: FileSignature, desc: "Win deals on the buyer side with a compelling offer letter.",
    fields: [
      { k: "property", l: "Property", t: "input", ph: "4bd modern home in Austin" },
      { k: "buyer_story", l: "Buyer story / why they love it", t: "textarea", ph: "Young family relocating, loved the backyard…" },
      { k: "offer_terms", l: "Offer highlights (price/terms)", t: "input", ph: "Full price, flexible closing, pre-approved" },
      { k: "tone", l: "Tone", t: "input", ph: "warm and sincere" },
    ] },
  { id: "comps", name: "Comparable-Aware Copy", icon: Scale, desc: "Position this listing as the smarter choice vs. nearby comps.",
    fields: [
      { k: "property", l: "This property", t: "textarea", ph: "3bd/2ba, $525k, renovated kitchen, $290/sqft" },
      { k: "comps", l: "Nearby comps (notes)", t: "textarea", ph: "Similar home down the street $560k, older kitchen…" },
      { k: "tone", l: "Tone", t: "input", ph: "confident" },
    ] },
  { id: "testimonial", name: "Review & Testimonial Writer", icon: Star, desc: "Turn rough feedback into polished reviews + a request message.",
    fields: [
      { k: "client", l: "Client name / initials", t: "input", ph: "The Garcias" },
      { k: "notes", l: "Rough feedback / notes", t: "textarea", ph: "Super responsive, sold over asking in 5 days…" },
      { k: "deal", l: "Transaction type", t: "input", ph: "Sold their home" },
    ] },
  { id: "buyer_match", name: "Buyer Match Messaging", icon: Users, desc: "Personalized outreach to past buyers who fit a new listing.",
    fields: [
      { k: "listing", l: "New listing summary", t: "textarea", ph: "2bd condo, South Loop, rooftop pool, $410k" },
      { k: "buyer", l: "Past buyer profile", t: "textarea", ph: "Bought a starter condo in 2022, wanted amenities…" },
      { k: "channel", l: "Channel", t: "select", opts: ["email", "text", "DM"] },
    ] },
  { id: "ad_copy", name: "Ad Copy Generator", icon: Megaphone, desc: "High-converting Facebook, Instagram & Google ad variations.",
    fields: [
      { k: "listing", l: "Listing", t: "textarea", ph: "2bd condo South Loop with rooftop pool" },
      { k: "standout", l: "Standout feature", t: "input", ph: "rooftop pool & skyline views" },
      { k: "platform", l: "Platform", t: "select", opts: ["Facebook", "Instagram", "Google"] },
      { k: "goal", l: "Goal", t: "input", ph: "book showings" },
    ] },
  { id: "follow_up", name: "Follow-Up Sequence Builder", icon: MailPlus, desc: "Multi-step email & text nurture sequences for leads.",
    fields: [
      { k: "listing", l: "Listing / context", t: "textarea", ph: "Open house lead for 123 Maple Ave" },
      { k: "lead", l: "Lead type", t: "input", ph: "Open house visitor, not pre-approved" },
      { k: "steps", l: "Number of steps", t: "input", ph: "4" },
    ] },
  { id: "performance_predictor", name: "Performance Predictor", icon: Gauge, desc: "Score listing copy's engagement and get improvement tips.",
    fields: [
      { k: "copy", l: "Listing copy", t: "textarea", ph: "Paste your description…" },
      { k: "target_buyer", l: "Target buyer", t: "input", ph: "first-time buyers" },
    ] },
  { id: "ab_test", name: "A/B Test Variants", icon: SplitSquareHorizontal, desc: "Generate two test variants to see what drives more interest.",
    fields: [
      { k: "copy", l: "Original copy", t: "textarea", ph: "Paste your description…" },
      { k: "angle", l: "Test angle", t: "input", ph: "lifestyle vs. value" },
    ] },
  { id: "emotional", name: "Emotional Storytelling", icon: Heart, desc: "Narrative-driven copy for luxury & unique homes.",
    fields: [
      { k: "property", l: "Property", t: "textarea", ph: "1920s restored craftsman with original details" },
      { k: "angle", l: "Unique soul of the home", t: "input", ph: "generations of memories, garden oasis" },
      { k: "tone", l: "Tone", t: "input", ph: "cinematic and warm" },
    ] },
  { id: "seasonal", name: "Seasonal & Market-Aware", icon: CalendarClock, desc: "Tune copy to the season and current market.",
    fields: [
      { k: "property", l: "Property", t: "textarea", ph: "3bd home with cozy fireplace" },
      { k: "season", l: "Season", t: "input", ph: "winter" },
      { k: "market", l: "Market condition", t: "input", ph: "low inventory, motivated buyers" },
    ] },
];

export default function ToolkitView() {
  const [active, setActive] = useState(TOOLS[0]);
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const pick = (t) => { setActive(t); setInputs({}); setResult(""); };
  const set = (k) => (e) => setInputs((p) => ({ ...p, [k]: e.target.value }));
  const setSel = (k) => (v) => setInputs((p) => ({ ...p, [k]: v }));

  const run = async () => {
    setLoading(true);
    setResult("");
    try {
      const { data } = await api.post("/tools/run", { tool: active.id, inputs });
      setResult(data.text);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1 space-y-2">
        {TOOLS.map((t) => (
          <button key={t.id} onClick={() => pick(t)} data-testid={`tool-${t.id}`}
            className={`w-full text-left flex items-start gap-3 px-3 py-3 rounded-lg border transition-colors ${active.id === t.id ? "border-slate-900 bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:bg-white"}`}>
            <t.icon className={`h-5 w-5 mt-0.5 shrink-0 ${active.id === t.id ? "text-slate-900" : "text-slate-400"}`} />
            <span className="text-sm font-medium leading-tight">{t.name}</span>
          </button>
        ))}
      </div>

      <div className="lg:col-span-3 space-y-5">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-semibold text-lg flex items-center gap-2"><active.icon className="h-5 w-5" /> {active.name}</h2>
          <p className="text-sm text-slate-500 mt-1">{active.desc}</p>
          <div className="space-y-4 mt-5">
            {active.fields.map((f) => (
              <div key={f.k}>
                <Label>{f.l}</Label>
                {f.t === "textarea" ? (
                  <Textarea data-testid={`tool-field-${f.k}`} value={inputs[f.k] || ""} onChange={set(f.k)} rows={3} className="mt-1.5" placeholder={f.ph} />
                ) : f.t === "select" ? (
                  <Select value={inputs[f.k] || f.opts[0]} onValueChange={setSel(f.k)}>
                    <SelectTrigger className="mt-1.5" data-testid={`tool-field-${f.k}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{f.opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input data-testid={`tool-field-${f.k}`} value={inputs[f.k] || ""} onChange={set(f.k)} className="mt-1.5" placeholder={f.ph} />
                )}
              </div>
            ))}
            <Button onClick={run} disabled={loading} data-testid="tool-run-btn" className="h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
              {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Writing…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate</>}
            </Button>
          </div>
        </div>
        {result && <OutputCard title={active.name} icon={active.icon} testid="tool-output" copyText={result}>{result}</OutputCard>}
      </div>
    </div>
  );
}
